import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../config/axios';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import { formatCurrency } from '../utils/themeUtils';
import { useToast } from '@chakra-ui/react';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, token } = useAuth();
  const { socket, onEvent } = useSocket();
  const toast = useToast();

  // Update unread count
  const updateUnreadCount = useCallback((notifList) => {
    const count = notifList.filter(n => !n.isRead).length;
    setUnreadCount(count);
  }, []);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const response = await api.get('/api/notifications');
      setNotifications(response.data.notifications);
      updateUnreadCount(response.data.notifications);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token, updateUnreadCount]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/api/notifications/${notificationId}/read`);

      setNotifications(prev => {
        const updated = prev.map(n =>
          n.id === notificationId ? { ...n, isRead: true } : n
        );
        updateUnreadCount(updated);
        return updated;
      });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await api.put('/api/notifications/read-all');

      setNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true }))
      );

      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/api/notifications/${notificationId}`);

      setNotifications(prev => {
        const updated = prev.filter(n => n.id !== notificationId);
        updateUnreadCount(updated);
        return updated;
      });
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  // Add new notification
  const addNotification = useCallback((notification) => {
    setNotifications(prev => {
      const updated = [notification, ...prev];
      updateUnreadCount(updated);
      return updated;
    });
  }, [updateUnreadCount]);

  // Clear all notifications
  const clearAllNotifications = async () => {
    try {
      await api.delete('/api/notifications/clear-all');
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
    }
  };

  // Filter notifications by type
  const getNotificationsByType = (type) => {
    return notifications.filter(n => n.type === type);
  };

  // Get notifications by priority
  const getNotificationsByPriority = (priority) => {
    return notifications.filter(n => n.priority === priority);
  };

  // Search notifications
  const searchNotifications = (query) => {
    if (!query) return notifications;

    return notifications.filter(n =>
      n.title.toLowerCase().includes(query.toLowerCase()) ||
      n.message.toLowerCase().includes(query.toLowerCase())
    );
  };

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    // Listen for new notifications
    const cleanupNotification = onEvent('notification', (data) => {
      addNotification({
        id: Date.now().toString(), // Temporary ID
        title: data.title,
        message: data.message,
        type: data.type || 'system',
        priority: data.priority || 'normal',
        isRead: false,
        createdAt: new Date().toISOString(),
        targetAudience: data.targetAudience || 'all',
        actionUrl: data.actionUrl,
        actionText: data.actionText
      });
      toast({
        title: data.title,
        description: data.message,
        status: data.priority === 'high' ? 'warning' : 'info',
        duration: 8000,
        isClosable: true,
        position: 'top-right'
      });
    });

    // Listen for donation updates
    const cleanupDonation = onEvent('donation-update', (data) => {
      addNotification({
        id: Date.now().toString(),
        title: 'Donation Update',
        message: `Your donation of ${formatCurrency(data.amount)} has been ${data.status}`,
        type: 'donation',
        priority: 'normal',
        isRead: false,
        createdAt: new Date().toISOString(),
        targetAudience: 'specific_users',
        actionUrl: '/dashboard/donations',
        actionText: 'View Details'
      });
      toast({
        title: 'Donation Status Updated',
        description: `Your donation of ${formatCurrency(data.amount)} is now ${data.status}.`,
        status: data.status === 'completed' ? 'success' : 'info',
        duration: 8000,
        isClosable: true,
        position: 'top-right'
      });
    });

    // Listen for event updates
    const cleanupEvent = onEvent('event-update', (data) => {
      addNotification({
        id: Date.now().toString(),
        title: 'Event Update',
        message: data.message,
        type: 'event',
        priority: 'normal',
        isRead: false,
        createdAt: new Date().toISOString(),
        targetAudience: 'all',
        actionUrl: '/events',
        actionText: 'View Events'
      });
      toast({
        title: 'Event Alert',
        description: data.message,
        status: 'info',
        duration: 6000,
        isClosable: true,
        position: 'top-right'
      });
    });

    // Listen for prayer request updates
    const cleanupPrayer = onEvent('prayer-update', (data) => {
      addNotification({
        id: Date.now().toString(),
        title: 'Prayer Request Update',
        message: `Your prayer request "${data.title}" has been ${data.status}`,
        type: 'prayer',
        priority: 'normal',
        isRead: false,
        createdAt: new Date().toISOString(),
        targetAudience: 'specific_users',
        actionUrl: '/dashboard/prayers',
        actionText: 'View Details'
      });
      toast({
        title: 'Prayer Request Update',
        description: `Your prayer request "${data.title}" is now ${data.status}.`,
        status: data.status === 'completed' ? 'success' : 'info',
        duration: 8000,
        isClosable: true,
        position: 'top-right'
      });
    });

    // Listen for booking updates
    const cleanupBooking = onEvent('booking-update', (data) => {
      addNotification({
        id: Date.now().toString(),
        title: 'Booking Update',
        message: `Your booking for "${data.bookingType}" has been ${data.status}`,
        type: 'booking',
        priority: 'normal',
        isRead: false,
        createdAt: new Date().toISOString(),
        targetAudience: 'specific_users',
        actionUrl: '/dashboard/bookings',
        actionText: 'View Details'
      });
      toast({
        title: 'Booking Status Updated',
        description: `Your booking for "${data.bookingType}" is now ${data.status}.`,
        status: data.status === 'confirmed' || data.status === 'completed' ? 'success' : 'info',
        duration: 8000,
        isClosable: true,
        position: 'top-right'
      });
    });

    // Listen for admin notifications
    const cleanupAdmin = onEvent('admin-notification', (data) => {
      if (user && (user.role === 'admin' || user.role === 'sub-admin')) {
        addNotification({
          id: Date.now().toString(),
          title: 'Admin Notification',
          message: data.message,
          type: 'system',
          priority: 'high',
          isRead: false,
          createdAt: new Date().toISOString(),
          targetAudience: 'admins',
          actionUrl: data.actionUrl,
          actionText: data.actionText
        });
        toast({
          title: 'Admin Dashboard Alert',
          description: data.message,
          status: 'warning',
          duration: 10000,
          isClosable: true,
          position: 'top-right'
        });
      }
    });

    return () => {
      cleanupNotification();
      cleanupDonation();
      cleanupEvent();
      cleanupPrayer();
      cleanupBooking();
      cleanupAdmin();
    };
  }, [socket, onEvent, user, addNotification, toast]);

  // Fetch notifications on mount and when user changes
  useEffect(() => {
    if (user && token) {
      fetchNotifications();
    }
  }, [user, token, fetchNotifications]);

  const value = {
    notifications,
    loading,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addNotification,
    clearAllNotifications,
    getNotificationsByType,
    getNotificationsByPriority,
    searchNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
